/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Ganturin Vladimir <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {CollectionViewer, DataSource, SelectionChange} from '@angular/cdk/collections';
import {FlatTreeControl} from '@angular/cdk/tree';
import {LazyCatalog} from 'compob2b-libs';
import {BehaviorSubject, merge, Observable, Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {CustomerCatalogService} from '../services/customer-catalog.service';

/**
 * Adapted code from angular material example
 * @see {@link https://material.angular.io/components/tree/examples#tree-dynamic}
 */
export class CustomerDynamicDataSource implements DataSource<LazyCatalog> {

  dataChange = new BehaviorSubject<LazyCatalog[]>([]);

  get data(): LazyCatalog[] {
    return this.dataChange.value;
  }

  set data(value: LazyCatalog[]) {
    this.treeControl.dataNodes = value;
    this.dataChange.next(value);

  }

  constructor(private treeControl: FlatTreeControl<LazyCatalog>,
              private readonly catalogService: CustomerCatalogService,
              private readonly unsubscribeFlag: Subject<boolean>
  ) {
  }

  connect(collectionViewer: CollectionViewer): Observable<LazyCatalog[]> {

    this.treeControl.expansionModel.changed.pipe(takeUntil(this.unsubscribeFlag))
      .subscribe((change: SelectionChange<LazyCatalog>) => this.handleTreeControl(change));

    return merge(collectionViewer.viewChange, this.dataChange).pipe(map(_ => this.data));
  }

  disconnect(collectionViewer: CollectionViewer): void {
  }

  /** Handle expand/collapse behaviors */
  handleTreeControl(change: SelectionChange<LazyCatalog>) {

    if (change.added) {
      change.added.forEach(node => this.toggleNode(node, true));
    }
    if (change.removed) {
      change.removed.slice().reverse().forEach(node => this.toggleNode(node, false));
    }
  }

  /**
   * Toggle the node, remove from display list
   */
  toggleNode(node: LazyCatalog, expand: boolean) {
    node.isLoading = true;

    this.catalogService.getLazyChildren(node.id).pipe(takeUntil(this.unsubscribeFlag))
      .subscribe(children => {
        const index = this.data.indexOf(node);
        if (!children || index < 0) { // If no children, or cannot find the node, no op
          return;
        }

        if (expand) {
          this.data.splice(index + 1, 0, ...children);
        } else {
          let count = 0;
          for (let i = index + 1; i < this.data.length
          && this.data[i].level > node.level; i++, count++) {
          }
          this.data.splice(index + 1, count);
        }
        // notify the change
        this.dataChange.next(this.data);
        node.isLoading = false;
      });
  }
}
